import type { ApplicationContext } from '@hc-frontend/core-context';
import { AdServer, CostModel, ProductType } from '@hc-frontend/core-types';
import { type FeaturedAd, FeaturedAds } from '@hc-frontend/core-types';
import {
  createErrorOperationResult,
  createSuccessOperationResult,
} from '@hc-frontend/core-utils-business-operation';
import { randomUUID } from 'crypto';
import { decode } from 'html-entities';

import { fetchAdButlerAds } from './fetch-ad-butler-ads-internal';
import { replaceHcMacros } from './replace-hc-macros';
import {
  AdButlerAdBodyDto,
  type FetchAdButlerRequestDto,
  type GetAdButlerAdsResult,
} from './types';

function encodePlusSignAndDecodeUri(text: string) {
  const plusSignEncodedUri = text.replace(/\+/g, '%20');
  return decodeURI(plusSignEncodedUri);
}

export function getAdButlerAds(
  url: string,
  log: ApplicationContext['log'],
): GetAdButlerAdsResult {
  return async (params) => {
    try {
      const clickUUID = randomUUID();

      const payload: FetchAdButlerRequestDto = {
        ID: params.adbutlerId,
        type: 'jsonr',
        setID: params.adbutlerSetId,
        _abdk_json: {
          state: params.prospect.location?.stateAcronym,
        },
        customParam1: `sessionId=${params.sessionId}&userId=${params.userId}`,
        customParam2: `domain_user_id=${params.domainUserId}&domain_session_id=${params.domainSessionId}`,
        customParam3: clickUUID,
      };

      log({
        type: 'info',
        context: 'server',
        message: 'getAdButlerAds',
        data: {
          payload: payload,
          url: url,
        },
      });

      const result = await fetchAdButlerAds(url, payload);

      if (result.status !== 200 || !result.body) {
        const error = new Error('Failed to fetch ad butler ads');
        log({
          type: 'error',
          context: 'server',
          message: 'failedToGetAdButlerAds',
          data: {
            rootCause: error,
          },
        });

        return createErrorOperationResult({
          errors: [
            {
              rootCause: error,
              code: 'failedToGetAdButlerAds',
            },
          ],
        });
      }

      if (result.body.status === 'NO_ADS') {
        log({
          type: 'error',
          context: 'server',
          message: 'noAdButlerAdsFound',
          data: {
            rootCause: new Error('No ad butler ads found'),
          },
        });

        return createSuccessOperationResult(
          FeaturedAds.parse({
            id: randomUUID(),
            adsSource: 'AdButler',
            searchMethod: undefined,
            segmentationFit: undefined,
            result: [],
          }),
        );
      }

      const response: FeaturedAds = {
        id: randomUUID(),
        adsSource: 'AdButler',
        searchMethod: undefined,
        segmentationFit: undefined,
        result: result.body.placements.map((placement, index) => {
          const decodedBody = decode(placement.body);

          const additionalData = AdButlerAdBodyDto.parse(
            JSON.parse(
              replaceHcMacros({
                content: decodedBody,
                product: ProductType.Enum.HEALTH,
                prospect: params.prospect,
              }),
            ),
          );

          const advertiserName = encodePlusSignAndDecodeUri(
            additionalData.ADVERTISER_NAME_ENCODED,
          );
          const campaignName = encodePlusSignAndDecodeUri(
            additionalData.CAMPAIGN_NAME_ENCODED,
          );

          const response: FeaturedAd = {
            id: randomUUID(),
            logo: additionalData.imageUrl,
            link: placement.redirect_url,
            title: additionalData.title,
            provider: {
              id: Number(additionalData.ADVERTISER_ID),
              name: advertiserName,
              displayUrl: additionalData.displayUrl,
            },
            campaignId: Number(additionalData.CAMPAIGN_ID),
            campaignName,
            creativeId: parseInt(placement.banner_id),
            groupId: Number(additionalData.ADVERTISER_ID),
            cpc: Number(additionalData.CPC),
            dealType: CostModel.Enum.CPC,
            content: additionalData.subtitle,
            adsSource: AdServer.Enum.AdButler,
            uniqueClickId: `${clickUUID}-${index}`,
          };

          log({
            type: 'info',
            context: 'server',
            message: 'successfullyGotAdButlerAds',
            data: {
              response: response,
            },
          });

          return response;
        }),
      };

      const parsedResponse = FeaturedAds.parse(response);
      return createSuccessOperationResult(parsedResponse);
    } catch (error) {
      log({
        type: 'error',
        context: 'server',
        message: 'failedToGetAdButlerAds',
        data: {
          rootCause: error as Error,
        },
      });

      return createErrorOperationResult({
        errors: [
          {
            rootCause: error as Error,
            code: 'failedToGetAdButlerAds',
          },
        ],
      });
    }
  };
}
